import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { OrderFlowTraceService } from 'src/app/core/services/telemetry/order-flow-trace.service';

/**
 * Contexto de TELEMETRIA do item cuja foto está aberta.
 *
 * Opcional e separado dos Inputs de apresentação de propósito: o visualizador é
 * genérico (recebe uma URL, um título e um texto) e não deve passar a exigir um
 * produto para funcionar. Opener que não passa `tracking` continua igual — só
 * emite os eventos sem o id do item.
 */
export interface ViewerTrackingContext {
	itemId?: any;
	itemType?: string;
	price?: number;
	/** De onde a foto foi aberta: 'menu-card', 'pizza', 'extra'… */
	opener?: string;
}

/**
 * Contraste da cor da loja contra o fundo PRETO do visualizador.
 *
 * A cor é configurável por restaurante e nada impede que seja preta — a Adega
 * Mendes é assim, e no green o botão vestido com ela virou um retângulo preto
 * sobre fundo preto: só o texto aparecia, sem forma de botão. Abaixo do mínimo
 * de 3:1 (WCAG, componentes gráficos) o botão troca para o acabamento branco.
 *
 * Aceita `#rgb`, `#rrggbb` e `rgb()/rgba()` — o ThemeService entrega hex, mas o
 * valor chega por `getComputedStyle` e pode vir normalizado.
 */
export function parseCssColor(value: string): [number, number, number] | null {
	if (!value) return null;
	const cor = value.trim();

	const hex = cor.replace('#', '');
	if (/^[0-9a-f]{3}$/i.test(hex)) {
		return [
			parseInt(hex[0] + hex[0], 16),
			parseInt(hex[1] + hex[1], 16),
			parseInt(hex[2] + hex[2], 16),
		];
	}
	if (/^[0-9a-f]{6}$/i.test(hex)) {
		return [
			parseInt(hex.slice(0, 2), 16),
			parseInt(hex.slice(2, 4), 16),
			parseInt(hex.slice(4, 6), 16),
		];
	}

	const rgb = cor.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
	if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];

	return null;
}

/** Luminância relativa (WCAG 2.x). */
export function relativeLuminance([r, g, b]: [number, number, number]): number {
	const canal = (v: number) => {
		const s = v / 255;
		return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
	};
	return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/**
 * Contra o preto o cálculo colapsa em `20 * L + 1`. Cor ilegível (ou não
 * reconhecida) devolve `true`: o acabamento branco é o padrão seguro.
 */
export function needsLightAction(cor: string, minContrast = 3): boolean {
	const rgb = parseCssColor(cor);
	if (!rgb) return true;
	return 20 * relativeLuminance(rgb) + 1 < minContrast;
}

/**
 * Substituto in-repo do ViewerModalComponent do ngx-ionic-image-viewer@0.7.4
 * (MIT © Simon Golms — github.com/SimonGolms/ngx-ionic-image-viewer).
 *
 * Por que existe: a lib nunca ganhou build Ivy (morre no Angular 13 junto com o
 * ngcc) e o original dependia de ion-slides, removido no Ionic 7. O zoom aqui é
 * implementado com eventos de toque nativos (pinch + double-tap + pan), sem deps.
 *
 * Mantém a API usada pelos openers do app (card/pizza/extra/pizza-nep/
 * pizza-intermediary): src, scheme, title, text, swipeToClose — e o cssClass
 * global 'ion-img-viewer' continua estilizando ion-text/ion-icon (global.scss).
 *
 * O botão de ação ("Quero esse") é OPT-IN: só aparece quando o opener passa
 * `actionLabel`. Os openers das telas de pizza/combo/complementos não passam —
 * lá o item já está aberto e o botão não teria para onde levar. Ao ser clicado
 * ele apenas fecha o modal com role 'select'; quem decide o destino é o opener,
 * via `onDidDismiss()`.
 */
@Component({
	selector: 'mp-viewer-modal',
	templateUrl: './viewer-modal.component.html',
	styleUrls: ['./viewer-modal.component.scss'],
})
export class ViewerModalComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() src: string;
	@Input() srcFallback?: string;
	@Input() alt?: string;
	@Input() scheme?: string;
	@Input() title?: string;
	@Input() titleSize?: string;
	@Input() text?: string;
	// Aceito por compatibilidade com a API antiga; todos os openers passam false.
	@Input() swipeToClose = false;

	// Botão de ação abaixo da foto. Sem `actionLabel` o rodapé segue como era.
	@Input() actionLabel?: string;
	@Input() actionDisabled = false;
	@Input() actionDisabledLabel?: string;

	// Contexto de telemetria; sem ele os eventos saem sem o id do item.
	@Input() tracking?: ViewerTrackingContext;

	// Marca da loja escura demais para o fundo preto: cai no acabamento branco.
	useLightAction = false;

	static readonly SELECT_ROLE = 'select';

	@ViewChild('img', { static: true }) imgRef: ElementRef<HTMLImageElement>;
	// Só para a telemetria: prova que o botão CHEGOU AO DOM. Ver ngAfterViewInit.
	@ViewChild('actionBtn') actionBtnRef?: ElementRef<HTMLButtonElement>;

	private scale = 1;
	private tx = 0;
	private ty = 0;
	private startScale = 1;
	private startDist = 0;
	private panStartX = 0;
	private panStartY = 0;
	private startTx = 0;
	private startTy = 0;
	private lastTapAt = 0;
	private pinching = false;

	// Telemetria da sessão de foto (ver ngAfterViewInit / ngOnDestroy).
	private openedAt = 0;
	private zoomUsed = false;
	private actionRendered = false;
	private selected = false;

	private static readonly MAX_SCALE = 4;
	private static readonly DOUBLE_TAP_SCALE = 2.5;
	private static readonly DOUBLE_TAP_MS = 300;

	constructor(
		private modalController: ModalController,
		private orderFlowTrace: OrderFlowTraceService,
	) {}

	ngOnInit(): void {
		if (this.scheme === 'dark') {
			// O modal é criado via modalController; o tema escuro vale só aqui dentro.
			this.imgRef.nativeElement.closest('mp-viewer-modal').classList.add('viewer-dark');
		}
		if (this.actionLabel) this.useLightAction = needsLightAction(this.brandColor());
		this.openedAt = Date.now();
	}

	/**
	 * DENOMINADOR do "Quero esse": a foto abriu, e o botão está (ou não) na tela.
	 *
	 * Roda no AfterViewInit, não no OnInit, porque o que se quer registrar é o
	 * DOM renderizado — `actionBtnRef` só existe depois da primeira detecção de
	 * mudanças. Medir a intenção do opener (`!!actionLabel`) seria mais fácil e
	 * mediria a coisa errada: um `*ngIf` quebrado, um CSS que esconde o botão ou
	 * um rodapé que não monta continuariam contando como "botão exibido", e a
	 * adoção despencaria sem que nada no instrumento acusasse a causa.
	 *
	 * É também o controle positivo do par: sem este evento vivo, "ninguém clica"
	 * é indistinguível de "a telemetria parou".
	 */
	ngAfterViewInit(): void {
		this.actionRendered = !!this.actionBtnRef;
		this.orderFlowTrace.emit('ClientItemPhotoOpened', {
			in: this.trackingIn(),
			out: {
				action_rendered: this.actionRendered,
				action_disabled: !!this.actionDisabled,
				light_action: this.useLightAction,
				has_description: !!this.text,
			},
		}, 'viewer-modal.ngAfterViewInit');
	}

	/**
	 * Desfecho da foto: 'select' (foi pelo botão) ou 'close' (saiu por qualquer
	 * outro caminho — X, backdrop, voltar do Android, swipe).
	 *
	 * COMPLEMENTAR ao evento de abertura, nunca substituto: se a aba morrer com o
	 * modal aberto, o ngOnDestroy não roda e este evento não sai. Por isso a taxa
	 * de uso do botão se calcula contra `ClientItemPhotoOpened`.
	 */
	ngOnDestroy(): void {
		this.orderFlowTrace.emit('ClientItemPhotoClosed', {
			in: this.trackingIn(),
			out: {
				outcome: this.selected ? 'select' : 'close',
				action_rendered: this.actionRendered,
				action_disabled: !!this.actionDisabled,
				ms_open: this.msOpen(),
				zoom_used: this.zoomUsed,
			},
		}, 'viewer-modal.ngOnDestroy');
	}

	/** Identificação do item na telemetria — igual nos três eventos da foto. */
	private trackingIn(): any {
		const t = this.tracking || {};
		return {
			item_id: t.itemId !== undefined ? t.itemId : null,
			item_type: t.itemType || null,
			price: t.price !== undefined ? t.price : null,
			opener: t.opener || 'desconhecido',
		};
	}

	private msOpen(): number {
		return this.openedAt ? Date.now() - this.openedAt : 0;
	}

	/**
	 * Cor da loja resolvida do CSS.
	 *
	 * Lê do `documentElement` — onde o ThemeService de fato escreve — e não só do
	 * host: no `ngOnInit` o modal ainda não está inserido no documento, e
	 * `getComputedStyle` de elemento solto devolve string VAZIA. Lendo apenas o
	 * host, toda loja caía no acabamento branco do fallback, inclusive as de cor
	 * com contraste de sobra. O host vem primeiro só para respeitar um eventual
	 * escopo mais específico; vazio, cai no documento.
	 */
	private brandColor(): string {
		try {
			const noHost = getComputedStyle(this.imgRef.nativeElement).getPropertyValue('--ion-color-main');
			if (noHost && noHost.trim()) return noHost;
			return getComputedStyle(document.documentElement).getPropertyValue('--ion-color-main');
		} catch {
			return '';
		}
	}

	closeModal(): void {
		this.modalController.dismiss();
	}

	get actionText(): string {
		return this.actionDisabled ? this.actionDisabledLabel || this.actionLabel : this.actionLabel;
	}

	// Fecha antes de qualquer navegação: quem abriu reage no onDidDismiss(), já
	// com o modal fora da tela.
	selectItem(): void {
		if (this.actionDisabled) return;
		this.selected = true;
		// NUMERADOR do "Quero esse", emitido ANTES do dismiss: o dismiss destrói o
		// componente, e o que roda depois dele corre com a destruição.
		this.orderFlowTrace.emit('ClientItemPhotoActionClicked', {
			in: this.trackingIn(),
			out: {
				ms_open: this.msOpen(),
				zoom_used: this.zoomUsed,
				light_action: this.useLightAction,
			},
		}, 'viewer-modal.selectItem');
		this.modalController.dismiss(undefined, ViewerModalComponent.SELECT_ROLE);
	}

	onImgError(): void {
		if (this.srcFallback && this.imgRef.nativeElement.src !== this.srcFallback) {
			this.imgRef.nativeElement.src = this.srcFallback;
		}
	}

	onTouchStart(ev: TouchEvent): void {
		if (ev.touches.length === 2) {
			this.pinching = true;
			this.startDist = this.touchDist(ev);
			this.startScale = this.scale;
		} else if (ev.touches.length === 1) {
			const now = Date.now();
			if (now - this.lastTapAt < ViewerModalComponent.DOUBLE_TAP_MS) {
				this.toggleZoom();
				this.lastTapAt = 0;
				return;
			}
			this.lastTapAt = now;
			if (this.scale > 1) {
				this.panStartX = ev.touches[0].clientX;
				this.panStartY = ev.touches[0].clientY;
				this.startTx = this.tx;
				this.startTy = this.ty;
			}
		}
	}

	onTouchMove(ev: TouchEvent): void {
		if (ev.touches.length === 2 && this.pinching) {
			ev.preventDefault();
			const ratio = this.touchDist(ev) / (this.startDist || 1);
			this.scale = Math.min(Math.max(this.startScale * ratio, 1), ViewerModalComponent.MAX_SCALE);
			if (this.scale > 1) this.zoomUsed = true;
			this.applyTransform();
		} else if (ev.touches.length === 1 && this.scale > 1) {
			ev.preventDefault();
			this.tx = this.startTx + (ev.touches[0].clientX - this.panStartX);
			this.ty = this.startTy + (ev.touches[0].clientY - this.panStartY);
			this.applyTransform();
		}
	}

	onTouchEnd(ev: TouchEvent): void {
		if (ev.touches.length < 2) this.pinching = false;
		if (this.scale <= 1.05) this.resetZoom();
	}

	onDoubleClick(): void {
		// Desktop: duplo clique espelha o double-tap.
		this.toggleZoom();
	}

	private toggleZoom(): void {
		if (this.scale > 1) {
			this.resetZoom();
		} else {
			this.scale = ViewerModalComponent.DOUBLE_TAP_SCALE;
			this.zoomUsed = true;
			this.applyTransform();
		}
	}

	private resetZoom(): void {
		this.scale = 1;
		this.tx = 0;
		this.ty = 0;
		this.applyTransform();
	}

	private applyTransform(): void {
		this.imgRef.nativeElement.style.transform =
			`translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
	}

	private touchDist(ev: TouchEvent): number {
		const dx = ev.touches[0].clientX - ev.touches[1].clientX;
		const dy = ev.touches[0].clientY - ev.touches[1].clientY;
		return Math.hypot(dx, dy);
	}
}
